import { useQuery } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import { useContext } from "react";
import { CampaignSetupContext } from "../../../context/CampaignSetupContext";
import { listRewards } from "../../../graphql/queries/listRewards";

const StreakConfigView = ({ data }) => {
  // Safely extract data with fallbacks
  const { campaignState } = useContext(CampaignSetupContext);
  const catalogRefs = data?.catalog_refs || [];
  const catalogIds = catalogRefs.map((ref) => ref.catalog_id);
  const reward_ids = data?.reward_ids || [];
  const title = data?.title || data?.streak_title || "";
  const description = data?.description || data?.streak_description || "";
  const trackProgressMode =
    data?.required_activities || data?.track_streak_progress || "";
  const activitiesConfiguration =
    data?.activities_configuration?.map((activity) => {
      return {
        ...activity,
        activity_name: campaignState?.activities?.find(
          (a) => a.id === activity.activity_id
        )?.activityDefinition?.name,
      };
    }) || [];
  const allowSimultaneous = data?.allow_simultaneous_streaks;
  const userCanRepeat = data?.user_can_repeat_streak;
  const maxStreaksPerUser = data?.max_streaks_per_user;

  // Fetch rewards filtered by catalog IDs (same as CatalogsSelectView)
  const {
    data: rewardData,
    loading: rewardLoading,
    error: rewardError,
  } = useQuery(listRewards, {
    variables: {
      filter: {
        isActive: true,
        catalogs: catalogIds,
      },
      page: 0,
      size: 1000,
    },
    skip: catalogIds.length === 0,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Safely extract reward details with error handling (same as CatalogsSelectView)
  const getRewardDetails = () => {
    try {
      const allRewardsForCatalogs = rewardData?.listRewards?.items || [];
      if (!Array.isArray(allRewardsForCatalogs)) {
        return [];
      }

      // Filter to show only selected rewards with all their details
      const selectedRewards = allRewardsForCatalogs.filter((reward) => {
        try {
          return reward?.id && reward_ids.includes(reward.id);
        } catch (error) {
          console.error("Error filtering reward:", error);
          return false;
        }
      });

      return selectedRewards
        .map((reward) => ({
          id: reward?.id,
          name: reward?.name,
          image: reward?.image || reward?.imageUrl,
          uuid: reward?.uuid || reward?.id,
          description: reward?.description,
          price: reward?.price,
          currency: reward?.currency,
          category: reward?.category,
        }))
        .filter((item) => item.id);
    } catch (error) {
      console.error("Error processing reward details:", error);
      return [];
    }
  };

  const rewardDetails = getRewardDetails();

  const isLoading = catalogIds.length > 0 && rewardLoading;

  const hasError = rewardError;

  // Format track progress mode for display
  const formatTrackProgressMode = (mode) => {
    if (!mode) return "-";

    const modeMap = {
      ALL: "Separately in each activity",
      ANY: "Combined across all activities",
    };

    return modeMap[mode] || mode;
  };

  // Format boolean values
  const formatBoolean = (value) => {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return value === "true" || value === 1 || value === "1" ? "Yes" : "No";
  };

  // Render reward information to match Figma design
  const renderRewardValue = () => {
    if (isLoading) {
      return <CircularProgress size={20} />;
    }

    if (hasError) {
      return (
        <span style={{ color: "#d32f2f", fontSize: "14px" }}>
          Error loading reward details
        </span>
      );
    }

    if (rewardDetails.length > 0) {
      return (
        <div>
          {rewardDetails.map((reward, index) => (
            <div key={reward.id || index} style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "14px", color: "#333", marginBottom: "4px" }}>
                {reward.name || "Unnamed Reward"}
              </div>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                <strong>Reward UUID:</strong> {reward.uuid || "N/A"}
              </div>
              {reward.image && (
                <img
                  src={reward.image}
                  alt={reward.name || "Reward"}
                  style={{
                    width: "60px",
                    height: "60px",
                    objectFit: "cover",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
            </div>
          ))}
        </div>
      );
    }

    return <span style={{ fontSize: "14px", color: "#666" }}>-</span>;
  };

  // Render activities configuration to match Figma design
  const renderActivitiesConfiguration = () => {
    if (!activitiesConfiguration || activitiesConfiguration.length === 0) {
      return <span style={{ fontSize: "14px", color: "#666" }}>-</span>;
    }

    return (
      <div>
        {activitiesConfiguration.map((activity, index) => (
          <div key={index} style={{ marginBottom: "12px" }}>
            <div
              style={{
                fontSize: "14px",
                color: "#333",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Activity {index + 1}:{" "}
              {activity?.activity_name ||
                activity?.activity_type ||
                "Unknown Activity"}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "16px",
                fontSize: "12px",
              }}
            >
              <div>
                <span style={{ fontWeight: "600", color: "#333" }}>
                  Activity label:{" "}
                </span>
                <span style={{ color: "#666" }}>
                  {activity?.activity_label || activity?.label_singular || "-"}
                </span>
              </div>

              <div>
                <span style={{ fontWeight: "600", color: "#333" }}>
                  Activity label plural:{" "}
                </span>
                <span style={{ color: "#666" }}>
                  {activity?.activity_label_plural || activity?.label_plural || "-"}
                </span>
              </div>

              <div>
                <span style={{ fontWeight: "600", color: "#333" }}>
                  Activity amount:{" "}
                </span>
                <span style={{ color: "#666" }}>
                  {activity?.activity_amount ||
                    activity?.target_points ||
                    activity?.amount ||
                    "-"}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render Joy Points
  const renderJoyPoints = () => {
    return (
      <>
        <div
          style={{
            fontSize: "14px",
            color: "#333",
            fontWeight: "600",
            marginBottom: "4px",
          }}
        >
          Reward:{" "}
          <span style={{ color: "#666", fontSize: "12px" }}>
            {data?.joy_points_value} Joy points amount
          </span>
        </div>
      </>
    );
  };
  const renderProgramPoints = () => {
    return (
      <>
        <div
          style={{
            fontSize: "14px",
            color: "#333",
            fontWeight: "600",
            marginBottom: "4px",
          }}
        >
          Reward:{" "}
          <span style={{ color: "#666", fontSize: "12px" }}>
            {data?.program_points_value} Program points amount
          </span>
        </div>
      </>
    );
  };

  return (
    <div
      style={{
        padding: "16px",
        backgroundColor: "#fff",
        border: "1px solid #D7E5F8",
        borderRadius: "8px",
      }}
    >
      {/* Streak Title */}
      {title && (
        <div style={{ marginBottom: "16px" }}>
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Streak title:{" "}
          </span>
          <span style={{ fontSize: "14px", color: "#333" }}>{title}</span>
        </div>
      )}

      {/* Streak Description */}
      {description && (
        <div style={{ marginBottom: "16px" }}>
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Streak description:{" "}
          </span>
          <span style={{ fontSize: "14px", color: "#333" }}>{description}</span>
        </div>
      )}

      {/* Reward */}
      {reward_ids.length > 0 && data.reward_mode == "REWARDS" && (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#333",
              marginBottom: "8px",
            }}
          >
            Reward:
          </div>
          {renderRewardValue()}
        </div>
      )}
      {data.reward_mode == "JOY_POINTS" && (
        <div style={{ marginBottom: "16px" }}>{renderJoyPoints()}</div>
      )}
      {data.reward_mode == "PROGRAM_POINTS" && (
        <div style={{ marginBottom: "16px" }}>{renderProgramPoints()}</div>
      )}

      {/* Track Streak Progress */}
      {trackProgressMode && (
        <div style={{ marginBottom: "16px" }}>
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Track streak progress:{" "}
          </span>
          <span style={{ fontSize: "14px", color: "#333" }}>
            {formatTrackProgressMode(trackProgressMode)}
          </span>
        </div>
      )}

      {/* Activities Configuration */}
      {activitiesConfiguration.length > 0 && (
        <div style={{ marginBottom: "16px" }}>{renderActivitiesConfiguration()}</div>
      )}

      {/* Bottom row with three columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "24px",
          fontSize: "12px",
        }}
      >
        {/* User Can Repeat Streak */}
        {typeof userCanRepeat !== "undefined" && (
          <div>
            <span style={{ fontWeight: "600", color: "#333" }}>
              user can repeat streak:{" "}
            </span>
            <span style={{ color: "#333" }}>{formatBoolean(userCanRepeat)}</span>
          </div>
        )}

        {/* Max Streaks Per User */}
        {typeof maxStreaksPerUser !== "undefined" && maxStreaksPerUser !== null && (
          <div>
            <span style={{ fontWeight: "600", color: "#333" }}>
              Max streaks per user:{" "}
            </span>
            <span style={{ color: "#333" }}>{maxStreaksPerUser}</span>
          </div>
        )}

        {/* Allow Simultaneous Streaks */}
        {typeof allowSimultaneous !== "undefined" && (
          <div>
            <span style={{ fontWeight: "600", color: "#333" }}>
              Allow simultaneous streaks:{" "}
            </span>
            <span style={{ color: "#333" }}>{formatBoolean(allowSimultaneous)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreakConfigView;
